<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card elevation="4" :loading="loading">
          <v-card-title class="text-indigo text-button">Mi perfil</v-card-title>

          <v-form ref="formRef" @submit.prevent="save">
            <v-container fluid>
              <v-row>
                <!-- ── datos institucionales (sólo lectura) ─────────────── -->
                <v-col cols="12">
                  <h4 class="text-button text-primary">Datos Institucionales</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="profile?.username"
                          label="Nombre de usuario"
                          prepend-inner-icon="mdi-account"
                          readonly
                          hint="Lo administra el proveedor de identidad."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="profile?.email"
                          label="Email"
                          prepend-inner-icon="mdi-email"
                          readonly
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          :model-value="rolesLabel"
                          label="Roles"
                          prepend-inner-icon="mdi-shield-account"
                          readonly
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <!-- ── datos de firma (editables) ───────────────────────── -->
                <v-col v-if="isPerson" cols="12">
                  <h4 class="text-button text-primary">Datos de Firma</h4>
                  <v-divider />
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.firstname"
                          label="Nombres"
                          prepend-inner-icon="mdi-account-edit"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('firstname')"
                          @update:model-value="errors.clear('firstname')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.lastname"
                          label="Apellidos"
                          prepend-inner-icon="mdi-account-edit"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('lastname')"
                          @update:model-value="errors.clear('lastname')"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-text-field
                          v-else
                          v-model="data.dni"
                          label="RUT"
                          prepend-inner-icon="mdi-card-account-details"
                          :disabled="saving"
                          :rules="[rules.required]"
                          :error-messages="errors.for('dni')"
                          hint="Sin puntos ni guión."
                          persistent-hint
                          @update:model-value="errors.clear('dni')"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="list-item-two-line" />
                        <v-file-input
                          v-else
                          v-model="signatureFile"
                          accept=".jpg,.jpeg,.png"
                          label="Rúbrica"
                          prepend-icon=""
                          prepend-inner-icon="mdi-draw"
                          show-size
                          :disabled="saving"
                          :error-messages="errors.for('signature')"
                          hint="Imagen que se estampa en los PDF firmados."
                          persistent-hint
                        />
                      </v-col>
                      <v-col cols="12" sm="6" lg="4">
                        <v-skeleton-loader v-if="loading" type="image" />
                        <v-sheet v-else-if="data.signature" elevation="1" class="pa-2">
                          <!-- <img> plano y no <v-img>: ver la nota en AgAppBar.vue. -->
                          <img
                            :src="data.signature"
                            alt="Rúbrica"
                            style="width: 100%; height: auto; display: block"
                          />
                        </v-sheet>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>

                <v-col v-else cols="12">
                  <v-alert type="info" variant="tonal" class="ma-4">
                    Esta cuenta es de tipo <b>aplicación</b>, así que no tiene datos de
                    firma personales que editar.
                  </v-alert>
                </v-col>
              </v-row>
            </v-container>

            <v-card-actions v-if="isPerson">
              <v-spacer />
              <v-btn
                type="submit"
                variant="text"
                color="success"
                class="mx-1"
                :loading="saving"
                :disabled="loading"
              >
                <v-icon class="mr-1">mdi-content-save</v-icon>Guardar
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import type { ProfileUpdatePayload } from "bancojs";
import { banco } from "@/api/client";
import { useApiErrors } from "@/composables/useApiErrors";
import { useFormErrors } from "@/composables/useFormErrors";
import { useNotificationsStore } from "@/stores/notifications";
import { useAuthStore } from "@/stores/auth";
import { roleLabel } from "@/utils/labels";

const auth = useAuthStore();
const { report } = useApiErrors();
const notifications = useNotificationsStore();
const errors = useFormErrors();

const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);

const loading = ref(true);
const saving = ref(false);
const profile = computed(() => auth.profile);

/**
 * `/my/profile` devuelve una unión discriminada por `type`. Una cuenta CLIENT no
 * tiene firstname/lastname/dni, así que el formulario de firma no aplica. La
 * versión anterior asumía que esos campos siempre existían.
 */
const isPerson = computed(() => profile.value?.type === "PERSON");

const data = ref({ firstname: "", lastname: "", dni: "", signature: "" });
const signatureFile = ref<File | File[] | null>(null);

const rolesLabel = computed(() =>
  (profile.value?.roles ?? []).map(roleLabel).join(", ") || "Sin roles"
);

const rules = { required: (val: unknown) => !!val || "Campo requerido." };

watch(signatureFile, async (file) => {
  const picked = Array.isArray(file) ? file[0] : file;
  if (!picked) return;
  try {
    data.value.signature = await toBase64(picked);
  } catch {
    notifications.error("No fue posible leer la imagen de la rúbrica.", "Rúbrica");
  }
});

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function load() {
  try {
    const me = await banco.user.profile.get();
    auth.setProfile(me);
    if (me.type === "PERSON") {
      data.value.firstname = me.firstname ?? "";
      data.value.lastname = me.lastname ?? "";
      data.value.dni = me.dni ?? "";
      data.value.signature = me.signature ?? "";
    }
  } catch (err) {
    report(err, { title: "Perfil", fallback: "Ocurrió un error al recuperar tu perfil." });
  } finally {
    loading.value = false;
  }
}

async function save() {
  const validation = await formRef.value?.validate();
  if (validation && !validation.valid) return;

  errors.reset();
  saving.value = true;

  /**
   * MyUserController liga ProfileDTO, cuyos CUATRO campos son obligatorios
   * (@NotBlank en los tres primeros, @NotNull en signature) y descarta todo lo
   * demás. La versión anterior enviaba el objeto User completo, del que el
   * servidor sólo leía estos cuatro.
   */
  const payload: ProfileUpdatePayload = {
    firstname: data.value.firstname,
    lastname: data.value.lastname,
    dni: data.value.dni,
    signature: data.value.signature,
  };

  try {
    auth.setProfile(await banco.user.profile.update(payload));
    notifications.success("Se guardaron los cambios en tu perfil.", "Guardado");
  } catch (err) {
    report(err, {
      title: "Perfil",
      fallback: "Ocurrió un error al guardar tu perfil.",
      form: errors,
    });
  } finally {
    saving.value = false;
  }
}

onMounted(load);
</script>
